import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'

import {
  createPreschoolWeeklySkillPlan,
  createPreschoolWeeklyTheme,
  fetchPreschoolSkill,
  fetchPreschoolWeeklySkillPlanForWeek,
  fetchPreschoolWeeklyTheme,
  updatePreschoolWeeklySkillPlan,
  updatePreschoolWeeklyTheme
} from 'api/preschoolEvaluation'
import { showToast } from 'app/helpers'

import { PRESCHOOL_LEVEL_OPTIONS, sequentialStrandLabel } from '../constants'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  spacer: { flexGrow: 1 },
  weekInfo: { color: theme.palette.text.secondary },
  card: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(1)
  },
  helpText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: theme.spacing(1)
  },
  goalCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  goalRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  focusRow: { marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }
}))

const genGoalId = () =>
  `g_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`

// Monday of the week containing `dateStr`, plus Friday + month key.
const computeWeek = (dateStr) => {
  const d = dayjs(dateStr)
  if (!d.isValid()) return { weekStart: '', weekEnd: '', monthKey: '' }
  const dow = d.day() // 0 = Sun .. 6 = Sat
  const diff = dow === 0 ? -6 : 1 - dow
  const monday = d.add(diff, 'day')
  return {
    weekStart: monday.format('YYYY-MM-DD'),
    weekEnd: monday.add(4, 'day').format('YYYY-MM-DD'),
    monthKey: monday.format('YYYY-MM')
  }
}

const WeekBuilder = () => {
  const classes = useStyles()

  const [level, setLevel] = useState('NUR')
  const [dateValue, setDateValue] = useState(dayjs().format('YYYY-MM-DD'))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [skills, setSkills] = useState([])

  // Weekly theme
  const [themeUuid, setThemeUuid] = useState(null)
  const [subthemeTitle, setSubthemeTitle] = useState('')
  const [subthemeTopics, setSubthemeTopics] = useState('')
  const [themePublished, setThemePublished] = useState(false)

  // Weekly skill plan
  const [planUuid, setPlanUuid] = useState(null)
  const [selectedSkillIds, setSelectedSkillIds] = useState([])
  const [spiralGoalsBySkillId, setSpiralGoalsBySkillId] = useState({})
  const [sequentialFocusBySkillId, setSequentialFocusBySkillId] = useState({})
  // LKG only: sequential skills are authored like spiral (admin-defined goals).
  const [sequentialGoalsBySkillId, setSequentialGoalsBySkillId] = useState({})
  const [numericFocus, setNumericFocus] = useState('')
  const [planPublished, setPlanPublished] = useState(false)

  const { weekStart, weekEnd, monthKey } = computeWeek(dateValue)

  // Load the fixed skill library once.
  useEffect(() => {
    const run = async () => {
      try {
        const data = await fetchPreschoolSkill({ queries: { limit: 100 } })
        setSkills(data?.results || (Array.isArray(data) ? data : []))
      } catch (e) {
        showToast('Failed to load skills', 'danger')
      }
    }
    run()
  }, [])

  const loadWeek = useCallback(
    async (notify = false) => {
      if (!weekStart) return
      setLoading(true)
      try {
        const [themeRes, planRes] = await Promise.all([
          fetchPreschoolWeeklyTheme({
            queries: { level, week_start_date: weekStart }
          }),
          fetchPreschoolWeeklySkillPlanForWeek({
            level,
            week_start_date: weekStart
          })
        ])
        const theme = (themeRes?.results || [])[0] || null
        const plan = (planRes?.results || [])[0] || null

        setThemeUuid(theme?.uuid || null)
        setSubthemeTitle(theme?.subtheme_title || '')
        setSubthemeTopics(theme?.subtheme_topics || '')
        setThemePublished(!!theme?.is_published)

        const selectedIds = (plan?.selected_skill_ids || []).map(String)
        setPlanUuid(plan?.uuid || null)
        setSelectedSkillIds(selectedIds)
        setNumericFocus(
          plan?.numeric_focus != null ? String(plan.numeric_focus) : ''
        )
        setSequentialFocusBySkillId(plan?.sequential_focus_by_skill_id || {})
        const goalsMap = {}
        ;(plan?.spiral || []).forEach((sp) => {
          goalsMap[String(sp.skill_id)] = (sp.goals || []).map((g) => ({
            goal_id: g.goal_id || genGoalId(),
            text: g.text || ''
          }))
        })
        const seqGoalsMap = {}
        ;(plan?.sequential || []).forEach((sp) => {
          seqGoalsMap[String(sp.skill_id)] = (sp.goals || []).map((g) => ({
            goal_id: g.goal_id || genGoalId(),
            text: g.text || ''
          }))
        })
        // Seed a stable goal_id for any selected skill the server returned no
        // goals for, so goalsFor/seqGoalsFor read a stored (stable) list rather
        // than minting a fresh id on every render (which drops field focus).
        selectedIds.forEach((sid) => {
          if (!goalsMap[sid] || !goalsMap[sid].length) {
            goalsMap[sid] = [{ goal_id: genGoalId(), text: '' }]
          }
          if (!seqGoalsMap[sid] || !seqGoalsMap[sid].length) {
            seqGoalsMap[sid] = [{ goal_id: genGoalId(), text: '' }]
          }
        })
        setSpiralGoalsBySkillId(goalsMap)
        setSequentialGoalsBySkillId(seqGoalsMap)
        setPlanPublished(!!plan?.is_published)

        if (notify) showToast('Week loaded', 'success')
      } catch (e) {
        showToast('Failed to load week', 'danger')
      } finally {
        setLoading(false)
      }
    },
    [level, weekStart]
  )

  // Auto-load whenever the cohort or week changes.
  useEffect(() => {
    loadWeek(false)
  }, [loadWeek])

  const isSelected = (id) => selectedSkillIds.includes(String(id))

  // Ensure a skill has a stable seeded goal row in the given map. Called when a
  // skill is first selected (and on load) so the goal TextField key (goal_id)
  // stays constant across renders — without this the fallback in goalsFor/
  // seqGoalsFor minted a fresh goal_id every render, remounting the field and
  // dropping focus after the first keystroke.
  const seedGoals = (setter, sid) =>
    setter((prev) =>
      prev[sid] && prev[sid].length
        ? prev
        : { ...prev, [sid]: [{ goal_id: genGoalId(), text: '' }] }
    )

  const toggleSkill = (id) => {
    const sid = String(id)
    const willSelect = !selectedSkillIds.includes(sid)
    setSelectedSkillIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    )
    if (willSelect) {
      seedGoals(setSpiralGoalsBySkillId, sid)
      seedGoals(setSequentialGoalsBySkillId, sid)
    }
  }

  const goalsFor = (sid) =>
    spiralGoalsBySkillId[sid] && spiralGoalsBySkillId[sid].length
      ? spiralGoalsBySkillId[sid]
      : [{ goal_id: genGoalId(), text: '' }]

  const setGoalText = (sid, idx, text) =>
    setSpiralGoalsBySkillId((prev) => {
      const goals = [...goalsFor(sid)]
      goals[idx] = { ...goals[idx], text }
      return { ...prev, [sid]: goals }
    })

  const addGoal = (sid) =>
    setSpiralGoalsBySkillId((prev) => ({
      ...prev,
      [sid]: [...goalsFor(sid), { goal_id: genGoalId(), text: '' }]
    }))

  const removeGoal = (sid, idx) =>
    setSpiralGoalsBySkillId((prev) => {
      const goals = [...goalsFor(sid)]
      goals.splice(idx, 1)
      return {
        ...prev,
        [sid]: goals.length ? goals : [{ goal_id: genGoalId(), text: '' }]
      }
    })

  const setSeqFocus = (sid, text) =>
    setSequentialFocusBySkillId((prev) => ({ ...prev, [sid]: text }))

  // LKG-only sequential goal editors (mirror the spiral goal helpers).
  const seqGoalsFor = (sid) =>
    sequentialGoalsBySkillId[sid] && sequentialGoalsBySkillId[sid].length
      ? sequentialGoalsBySkillId[sid]
      : [{ goal_id: genGoalId(), text: '' }]

  const setSeqGoalText = (sid, idx, text) =>
    setSequentialGoalsBySkillId((prev) => {
      const goals = [...seqGoalsFor(sid)]
      goals[idx] = { ...goals[idx], text }
      return { ...prev, [sid]: goals }
    })

  const addSeqGoal = (sid) =>
    setSequentialGoalsBySkillId((prev) => ({
      ...prev,
      [sid]: [...seqGoalsFor(sid), { goal_id: genGoalId(), text: '' }]
    }))

  const removeSeqGoal = (sid, idx) =>
    setSequentialGoalsBySkillId((prev) => {
      const goals = [...seqGoalsFor(sid)]
      goals.splice(idx, 1)
      return {
        ...prev,
        [sid]: goals.length ? goals : [{ goal_id: genGoalId(), text: '' }]
      }
    })

  const buildPayloads = (publish) => {
    const spiralSelected = skills.filter(
      (s) => s.type === 'spiral' && isSelected(s.uuid)
    )
    const sequentialSelected = skills.filter(
      (s) => s.type === 'sequential' && isSelected(s.uuid)
    )

    const spiral = spiralSelected.map((s) => ({
      skill_id: String(s.uuid),
      goals: goalsFor(String(s.uuid))
        .filter((g) => (g.text || '').trim())
        .map((g) => ({ goal_id: g.goal_id, text: g.text.trim() }))
    }))

    // LKG authors sequential like spiral (admin-defined goals); other levels
    // use a single focus text per sequential skill.
    const isLkg = level === 'LKG'
    const seqFocus = {}
    const sequential = []
    if (isLkg) {
      sequentialSelected.forEach((s) => {
        sequential.push({
          skill_id: String(s.uuid),
          goals: seqGoalsFor(String(s.uuid))
            .filter((g) => (g.text || '').trim())
            .map((g) => ({ goal_id: g.goal_id, text: g.text.trim() }))
        })
      })
    } else {
      sequentialSelected.forEach((s) => {
        seqFocus[String(s.uuid)] =
          sequentialFocusBySkillId[String(s.uuid)] || ''
      })
    }

    const themePayload = {
      uuid: themeUuid,
      level,
      month_key: monthKey,
      week_start_date: weekStart,
      week_end_date: weekEnd,
      subtheme_title: subthemeTitle,
      subtheme_topics: subthemeTopics,
      is_published: publish ? true : themePublished
    }

    const planPayload = {
      uuid: planUuid,
      level,
      month_key: monthKey,
      week_start_date: weekStart,
      week_end_date: weekEnd,
      selected_skill_ids: selectedSkillIds,
      spiral,
      sequential,
      sequential_focus_by_skill_id: seqFocus,
      numeric_focus: numericFocus.trim() === '' ? null : numericFocus,
      is_published: publish ? true : planPublished
    }

    return { themePayload, planPayload }
  }

  const save = async (publish) => {
    // A draft may be saved incomplete — the subtheme title can be filled in
    // later. Only publishing the week requires it. (The backend accepts a blank
    // subtheme_title; this guard was silently blocking every title-less draft.)
    if (publish && !subthemeTitle.trim()) {
      showToast('Add a subtheme title before publishing', 'danger')
      return
    }
    setSaving(true)
    try {
      const { themePayload, planPayload } = buildPayloads(publish)
      const themeRes = themeUuid
        ? await updatePreschoolWeeklyTheme(themePayload)
        : await createPreschoolWeeklyTheme(themePayload)
      const planRes = planUuid
        ? await updatePreschoolWeeklySkillPlan(planPayload)
        : await createPreschoolWeeklySkillPlan(planPayload)
      setThemeUuid(themeRes?.uuid || themeUuid)
      setPlanUuid(planRes?.uuid || planUuid)
      if (publish) {
        setThemePublished(true)
        setPlanPublished(true)
      }
      showToast(publish ? 'Week published' : 'Draft saved', 'success')
    } catch (e) {
      showToast(e?.message || 'Save failed', 'danger')
    } finally {
      setSaving(false)
    }
  }

  const isLkgLevel = level === 'LKG'
  const sequentialSkills = skills.filter((s) => s.type === 'sequential')
  const spiralSkills = skills.filter((s) => s.type === 'spiral')
  const selectedSpiralSkills = spiralSkills.filter((s) => isSelected(s.uuid))
  const selectedSequentialSkills = sequentialSkills.filter((s) =>
    isSelected(s.uuid)
  )

  return (
    <Box className={classes.root}>
      {/* Top bar */}
      <Box className={classes.topBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {Object.entries(PRESCHOOL_LEVEL_OPTIONS).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Pick any date"
          type="date"
          size="small"
          variant="outlined"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Typography variant="body2" className={classes.weekInfo}>
          Week: <strong>{weekStart}</strong> → <strong>{weekEnd}</strong>
          {'  '}· Month: {monthKey}
          {planPublished || themePublished ? '  · Published' : '  · Draft'}
        </Typography>

        <Box className={classes.spacer} />

        {loading ? <CircularProgress size={22} /> : null}
        <Button variant="outlined" onClick={() => loadWeek(true)}>
          Reload
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={() => save(false)}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => save(true)}
          // Distinct "go live" colour so it doesn't read as disabled next to the
          // orange Save Draft. Style dropped while saving so the disabled grey shows.
          style={
            saving ? undefined : { backgroundColor: '#2d6a4f', color: '#fff' }
          }
        >
          Publish Week
        </Button>
      </Box>

      <Grid container spacing={2}>
        {/* Weekly theme */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" className={classes.card}>
            <Typography className={classes.sectionTitle}>
              Weekly Theme
            </Typography>
            <TextField
              label="Subtheme title"
              fullWidth
              variant="outlined"
              size="small"
              margin="dense"
              inputProps={{ maxLength: 200 }}
              value={subthemeTitle}
              onChange={(e) => setSubthemeTitle(e.target.value)}
            />
            <TextField
              label="Subtheme topics"
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              size="small"
              margin="dense"
              // history.css ships a global, unscoped `textarea { position:
              // absolute }` rule that leaks app-wide and pulls this field out
              // of flow, collapsing the outlined wrapper to ~1 row. Pin it back
              // into normal flow so the wrapper grows to fit all 5 rows.
              inputProps={{ maxLength: 1500, style: { position: 'static' } }}
              value={subthemeTopics}
              onChange={(e) => setSubthemeTopics(e.target.value)}
            />
          </Paper>
        </Grid>

        {/* Number Sense focus */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" className={classes.card}>
            <Typography className={classes.sectionTitle}>
              Weekly Skill Plan
            </Typography>
            <Typography className={classes.helpText}>
              Teachers see only published weeks.
            </Typography>
            <TextField
              label={
                level === 'LKG'
                  ? 'Number Sense focus'
                  : 'Number Sense focus (number)'
              }
              type={level === 'LKG' ? 'text' : 'number'}
              fullWidth
              variant="outlined"
              size="small"
              margin="dense"
              value={numericFocus}
              onChange={(e) => setNumericFocus(e.target.value)}
            />
            <Typography className={classes.helpText}>
              Teacher evaluates against the fixed goal: Counts objects and
              recognises numerals.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Skill selection */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>
        Select skills for this week
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" className={classes.card}>
            <Typography className={classes.sectionTitle}>
              Sequential ({sequentialStrandLabel(level)})
            </Typography>
            <Typography className={classes.helpText}>
              {isLkgLevel
                ? 'Admin defines 1+ goals per selected skill. Teacher writes a free-text observation per goal.'
                : 'Admin sets focus text. Teacher writes evaluations for the fixed goals.'}
            </Typography>
            {sequentialSkills.map((s) => (
              <Box key={s.uuid}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected(s.uuid)}
                      onChange={() => toggleSkill(s.uuid)}
                      color="primary"
                    />
                  }
                  label={s.name}
                />
                {isSelected(s.uuid) && isLkgLevel ? (
                  <Box className={classes.goalCard}>
                    <Typography className={classes.helpText}>
                      Goals (min 1)
                    </Typography>
                    {seqGoalsFor(String(s.uuid)).map((g, idx) => (
                      <Box key={g.goal_id} className={classes.goalRow}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          margin="dense"
                          placeholder="Goal text"
                          value={g.text}
                          onChange={(e) =>
                            setSeqGoalText(String(s.uuid), idx, e.target.value)
                          }
                        />
                        <IconButton
                          size="small"
                          aria-label="remove goal"
                          onClick={() => removeSeqGoal(String(s.uuid), idx)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addSeqGoal(String(s.uuid))}
                    >
                      Add goal
                    </Button>
                  </Box>
                ) : isSelected(s.uuid) ? (
                  <Box className={classes.focusRow}>
                    <TextField
                      label="Focus"
                      placeholder="Write focus..."
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={sequentialFocusBySkillId[String(s.uuid)] || ''}
                      onChange={(e) =>
                        setSeqFocus(String(s.uuid), e.target.value)
                      }
                    />
                  </Box>
                ) : null}
              </Box>
            ))}
            {sequentialSkills.length === 0 ? (
              <Typography className={classes.helpText}>None</Typography>
            ) : null}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" className={classes.card}>
            <Typography className={classes.sectionTitle}>
              Spiral (Observations)
            </Typography>
            <Typography className={classes.helpText}>
              Admin defines 1+ goals per selected spiral skill. Teacher writes
              evaluation per goal.
            </Typography>
            {spiralSkills.map((s) => (
              <FormControlLabel
                key={s.uuid}
                control={
                  <Checkbox
                    checked={isSelected(s.uuid)}
                    onChange={() => toggleSkill(s.uuid)}
                    color="primary"
                  />
                }
                label={s.name}
              />
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Sequential focus reminder if a language skill is selected but blank */}
      {selectedSequentialSkills.length > 0 ? null : null}

      {/* Spiral goal editors */}
      {selectedSpiralSkills.length > 0 ? (
        <>
          <Typography
            className={classes.sectionTitle}
            style={{ marginTop: 16 }}
          >
            Spiral details (goals per selected skill)
          </Typography>
          {selectedSpiralSkills.map((s) => {
            const sid = String(s.uuid)
            const goals = goalsFor(sid)
            return (
              <Paper key={sid} variant="outlined" className={classes.goalCard}>
                <Typography className={classes.sectionTitle}>
                  {s.name}
                </Typography>
                <Typography className={classes.helpText}>
                  Goals (min 1)
                </Typography>
                {goals.map((g, idx) => (
                  <Box key={g.goal_id} className={classes.goalRow}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      margin="dense"
                      placeholder="Goal text"
                      value={g.text}
                      onChange={(e) => setGoalText(sid, idx, e.target.value)}
                    />
                    <IconButton
                      size="small"
                      aria-label="remove goal"
                      onClick={() => removeGoal(sid, idx)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addGoal(sid)}
                >
                  Add goal
                </Button>
              </Paper>
            )
          })}
        </>
      ) : null}
    </Box>
  )
}

export default WeekBuilder
